import { Capacitor } from '@capacitor/core';

/*
The PKCE code verifier belongs to one in-flight login rather than to the installation, so it does
not go through `storage.ts`. Keeping it there caused two different failures, both surfacing as
"Missing code verifier when authenticating with code" (CONTROL-PANEL-80, POLYLINO-G0):

- On web it shared a single localStorage key with every other tab, so two tabs signing in at once
  overwrote each other's verifier. Both exchanges then failed and neither tab could finish, so both
  kept bouncing through the auth server. sessionStorage is per-tab and survives the redirects a
  login makes, which is the scope a verifier actually needs.
- On native there are no tabs, but the write has to be synchronous: `createLoginLink` navigates to
  the auth server without awaiting it, so the async Preferences bridge call could lose the race.

Both stores are synchronous, which is what makes that un-awaited write sound. This module exists so
that the key and the choice of store live in exactly one place.
 */
const STORAGE_KEY = 'ilt-auth.code-verifier';

/*
Resolved lazily rather than at module scope: the education app renders this on the server, where
neither store exists.
 */
const store = (): Storage => (Capacitor.isNativePlatform() ? localStorage : sessionStorage);

export const saveCodeVerifier = (codeVerifier: string): void => {
  store().setItem(STORAGE_KEY, codeVerifier);
};

/**
 * Reads the verifier and clears it, so a code can only ever be exchanged once.
 */
export const takeCodeVerifier = (): string | null => {
  const codeVerifierStore = store();
  const codeVerifier = codeVerifierStore.getItem(STORAGE_KEY);
  codeVerifierStore.removeItem(STORAGE_KEY);

  return codeVerifier;
};

export const hasCodeVerifier = (): boolean => store().getItem(STORAGE_KEY) !== null;
